import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Car } from "lucide-react";

const emptyVehicle = {
  customer_id: "",
  make: "",
  model: "",
  year: new Date().getFullYear(),
  license_plate: "",
  vin: "",
  mileage: 0,
  color: "",
};

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyVehicle);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    const [v, c] = await Promise.all([api.get("/vehicles"), api.get("/customers")]);
    setVehicles(v.data);
    setCustomers(c.data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const customerMap = Object.fromEntries(customers.map((c) => [c.id, c]));

  const openCreate = () => {
    if (customers.length === 0) {
      toast.error("Add a customer first before adding a vehicle.");
      return;
    }
    setEditing(null);
    setForm({ ...emptyVehicle, customer_id: customers[0].id });
    setDialogOpen(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    setForm({
      customer_id: v.customer_id,
      make: v.make,
      model: v.model,
      year: v.year,
      license_plate: v.license_plate || "",
      vin: v.vin || "",
      mileage: v.mileage || 0,
      color: v.color || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.customer_id || !form.make.trim() || !form.model.trim()) {
      toast.error("Customer, make, and model are required");
      return;
    }
    try {
      const payload = {
        ...form,
        year: parseInt(form.year) || 0,
        mileage: parseInt(form.mileage) || 0,
      };
      if (editing) {
        await api.put(`/vehicles/${editing.id}`, payload);
        toast.success("Vehicle updated");
      } else {
        await api.post("/vehicles", payload);
        toast.success("Vehicle added");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error("Failed to save vehicle");
    }
  };

  const del = async () => {
    try {
      await api.delete(`/vehicles/${confirmDelete.id}`);
      toast.success("Vehicle deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error("Failed to delete vehicle");
    }
  };

  return (
    <div>
      <PageHeader
        testid="vehicles-header"
        title="Vehicles"
        subtitle="Categorized by make and model — every ride that rolls through the bay."
        actions={
          <Button
            data-testid="add-vehicle-btn"
            onClick={openCreate}
            className="bg-[#1A1A1A] hover:bg-[#2D2D2D] text-white rounded-xl font-medium"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Vehicle
          </Button>
        }
      />

      {loading ? (
        <Card className="p-10 text-center text-slate-muted rounded-2xl border-warm">Loading…</Card>
      ) : vehicles.length === 0 ? (
        <EmptyState
          testid="vehicles-empty"
          image="https://images.unsplash.com/photo-1603039548092-fff996c2441c?crop=entropy&cs=srgb&fm=jpg&w=400&q=70"
          title="No vehicles registered"
          description="Add a vehicle to a customer so you can create work orders."
          action={
            <Button
              onClick={openCreate}
              className="bg-[#1A1A1A] hover:bg-[#2D2D2D] text-white rounded-xl"
              data-testid="empty-add-vehicle"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Vehicle
            </Button>
          }
        />
      ) : (
        (() => {
          // Group vehicles by Make + Model (case-insensitive), sort groups by count desc
          const groups = {};
          for (const v of vehicles) {
            const make = (v.make || "Unknown").trim() || "Unknown";
            const model = (v.model || "Unknown").trim() || "Unknown";
            const key = `${make.toLowerCase()}|${model.toLowerCase()}`;
            if (!groups[key]) groups[key] = { make, model, vehicles: [] };
            groups[key].vehicles.push(v);
          }
          const groupList = Object.values(groups).sort(
            (a, b) =>
              b.vehicles.length - a.vehicles.length ||
              `${a.make} ${a.model}`.localeCompare(`${b.make} ${b.model}`)
          );

          return (
            <div className="space-y-6" data-testid="vehicles-categorized">
              {groupList.map((g) => {
                const groupKey = `${g.make}-${g.model}`.replace(/\s+/g, "-");
                return (
                  <Card
                    key={groupKey}
                    className="rounded-2xl border-warm shadow-card overflow-hidden"
                    data-testid={`vehicle-group-${groupKey}`}
                  >
                    <div className="flex items-center justify-between px-6 py-4 bg-[#1A1A1A] text-white">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#E5A82E]/20 flex items-center justify-center text-[#E5A82E]">
                          <Car className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="font-heading text-lg font-semibold leading-tight">
                            {g.make} · {g.model}
                          </div>
                          <div className="text-xs text-white/60 uppercase tracking-widest">
                            Make · Model
                          </div>
                        </div>
                      </div>
                      <span
                        className="font-mono text-sm font-semibold bg-[#E5A82E]/20 border border-[#E5A82E]/40 text-[#E5A82E] rounded-full px-3 py-1"
                        data-testid={`vehicle-group-count-${groupKey}`}
                      >
                        {g.vehicles.length} vehicle{g.vehicles.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    <Table>
                      <TableHeader className="bg-[#F3F2EE]">
                        <TableRow>
                          <TableHead className="text-[#3D405B] font-semibold uppercase tracking-wider text-xs">Plate</TableHead>
                          <TableHead className="text-[#3D405B] font-semibold uppercase tracking-wider text-xs">Year</TableHead>
                          <TableHead className="text-[#3D405B] font-semibold uppercase tracking-wider text-xs">Owner</TableHead>
                          <TableHead className="text-[#3D405B] font-semibold uppercase tracking-wider text-xs">VIN</TableHead>
                          <TableHead className="text-[#3D405B] font-semibold uppercase tracking-wider text-xs">Mileage</TableHead>
                          <TableHead />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {g.vehicles.map((v) => (
                          <TableRow key={v.id} data-testid={`vehicle-row-${v.id}`} className="hover:bg-[#FAF9F6]">
                            <TableCell>
                              {v.license_plate ? (
                                <span className="inline-block font-mono text-sm font-semibold text-[#1A1A1A] bg-[#F4D35E]/30 border border-[#E5A82E]/40 rounded-md px-2 py-0.5">
                                  {v.license_plate.toUpperCase()}
                                </span>
                              ) : (
                                <span className="text-slate-muted italic">No plate</span>
                              )}
                            </TableCell>
                            <TableCell className="text-[#2B2D42]">
                              {v.year || "—"}
                              {v.color ? <span className="text-xs text-slate-muted ml-2">{v.color}</span> : null}
                            </TableCell>
                            <TableCell className="text-[#2B2D42]">
                              {customerMap[v.customer_id]?.name || "—"}
                            </TableCell>
                            <TableCell className="font-mono text-xs text-slate-muted">
                              {v.vin || "—"}
                            </TableCell>
                            <TableCell className="text-[#2B2D42]">
                              {v.mileage ? `${v.mileage.toLocaleString()} mi` : "—"}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  data-testid={`edit-vehicle-${v.id}`}
                                  onClick={() => openEdit(v)}
                                  className="rounded-lg text-[#3D405B] hover:bg-[#F3F2EE]"
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  data-testid={`delete-vehicle-${v.id}`}
                                  onClick={() => setConfirmDelete(v)}
                                  className="rounded-lg text-[#E07A5F] hover:bg-[#FAE8E4]"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Card>
                );
              })}
            </div>
          );
        })()
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="vehicle-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editing ? "Edit Vehicle" : "Add Vehicle"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-semibold text-[#3D405B]">Owner *</Label>
              <Select
                value={form.customer_id}
                onValueChange={(val) => setForm({ ...form, customer_id: val })}
              >
                <SelectTrigger
                  data-testid="vehicle-customer-select"
                  className="mt-1.5 rounded-lg border-2 border-warm"
                >
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-sm font-semibold text-[#3D405B]">Year *</Label>
                <Input
                  data-testid="vehicle-year-input"
                  type="number"
                  value={form.year}
                  onChange={(e) => setForm({ ...form, year: e.target.value })}
                  className="mt-1.5 rounded-lg border-2 border-warm"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-sm font-semibold text-[#3D405B]">Make *</Label>
                <Input
                  data-testid="vehicle-make-input"
                  value={form.make}
                  onChange={(e) => setForm({ ...form, make: e.target.value })}
                  placeholder="Toyota"
                  className="mt-1.5 rounded-lg border-2 border-warm"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#3D405B]">Model *</Label>
              <Input
                data-testid="vehicle-model-input"
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
                placeholder="Camry"
                className="mt-1.5 rounded-lg border-2 border-warm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold text-[#3D405B]">License Plate</Label>
                <Input
                  data-testid="vehicle-plate-input"
                  value={form.license_plate}
                  onChange={(e) => setForm({ ...form, license_plate: e.target.value })}
                  className="mt-1.5 rounded-lg border-2 border-warm font-mono"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-[#3D405B]">Color</Label>
                <Input
                  data-testid="vehicle-color-input"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="mt-1.5 rounded-lg border-2 border-warm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold text-[#3D405B]">VIN</Label>
                <Input
                  data-testid="vehicle-vin-input"
                  value={form.vin}
                  onChange={(e) => setForm({ ...form, vin: e.target.value })}
                  className="mt-1.5 rounded-lg border-2 border-warm font-mono"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-[#3D405B]">Mileage</Label>
                <Input
                  data-testid="vehicle-mileage-input"
                  type="number"
                  value={form.mileage}
                  onChange={(e) => setForm({ ...form, mileage: e.target.value })}
                  className="mt-1.5 rounded-lg border-2 border-warm"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="rounded-xl border-2 border-warm"
              data-testid="cancel-vehicle-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              className="bg-[#1A1A1A] hover:bg-[#2D2D2D] text-white rounded-xl"
              data-testid="save-vehicle-btn"
            >
              {editing ? "Save Changes" : "Add Vehicle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete vehicle?</AlertDialogTitle>
            <AlertDialogDescription>
              Every repair order for this vehicle will also be removed from the Repair Orders and Parts-to-Order pages. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={del}
              className="bg-[#E07A5F] hover:bg-[#C86A4F] text-white rounded-xl"
              data-testid="confirm-delete-vehicle"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
